import { useState, useEffect, useCallback } from "react";
import { RootState } from "../../store";
import { TextEnum } from "../../constants/text";
import { TextBox } from "../../constants/features/input";
import { DataTable } from "../../constants/features/table";
import { PushMessageColumns } from "../../constants/features/columns";
import { MessageModal } from "../../constants/features/modal";
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
  useDebounce,
} from "../../constants/global-hooks";
import {
  useHandlePushMessagePageChange,
  useHandlePushMessageSearchTextChange,
  useHandleGetAllPushMessage,
  useHandlePushMessageAlertMessageChange,
} from "../../constants/utils";
import "./push-message.scss";
import { DeciderButtonsTwoOptions } from "../../constants/features/buttons";
import {
  resetPushMessage,
  setPushMessageIsSent,
  setPushMessagePage,
  setPushMessageSelectedId,
} from "../../constants/reducers/push-message.reducer";
import { PushMessage } from "../../constants/entity";
import { getAllPushMessage } from "../../actions/push-message";

export const PushMessagePage = () => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { pushMessages, pageCount, page, searchText, isSent, errorMessage } =
    useAppSelector((state: RootState) => state.pushMessage);

  const [searchTerm, setSearchTerm] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [state, setState] = useState({
    searchTerm: false,
    isHovered: false,
    decider: 1,
  });

  const handleAlertMessage = useHandlePushMessageAlertMessageChange(dispatch);

  useEffect(() => {
    if (searchText) {
      setSearchTerm(true);
    }
  }, [searchText]);

  const getPushMessages = useHandleGetAllPushMessage();

  const debouncedGetPushMessages = useDebounce(
    getPushMessages,
    searchTerm ? 500 : 0,
  );

  const handleGetOne = useCallback(
    (record: PushMessage) => {
      navigate(`/push-message/${record.id}`);
    },
    [navigate],
  );

  useEffect(() => {
    const fetch = async () => {
      await getAllPushMessage(state.decider === 2);
    };
    fetch();
  }, [debouncedGetPushMessages, page, searchText, isSent, state.decider]);

  const handleDeciderChange = (value: number) => {
    setState((prevState) => ({ ...prevState, decider: value }));
    dispatch(setPushMessagePage(1));
  };
  useEffect(() => {
    if (state.decider === 2) {
      dispatch(setPushMessageIsSent(true));
      setState((prevState) => ({ ...prevState, searchTerm: false }));
    }
    if (state.decider === 1) {
      dispatch(setPushMessageIsSent(false));
      setState((prevState) => ({ ...prevState, searchTerm: false }));
    }
  }, [state.decider, dispatch]);

  const handleModalVisible = (id?: number) => {
    if (modalVisible === false) {
      dispatch(resetPushMessage());
    }
    setModalVisible(!modalVisible);
    if (id) {
      dispatch(setPushMessageSelectedId(id));
    }
  };

  return (
    <div>
      <main className="main-page">
        <div className="title">{TextEnum.PUSH_MESSAGE_PAGE_TITLE}</div>
        <div>
          <div className="buttons-container">
            <div className="push-message-buttons">
              <DeciderButtonsTwoOptions
                decider={state.decider}
                handleChange={handleDeciderChange}
                defaultButton={TextEnum.TIMED_BUTTON_LABEL}
                secondButton={TextEnum.SENT_BUTTON_LABEL}
              />
            </div>
            <TextBox
              value={searchText}
              onChange={useHandlePushMessageSearchTextChange(dispatch)}
              type={"input"}
              customErrorMessage={""}
              setDecider={() => null}
              placeholder={TextEnum.SEARCH}
              search={true}
            />
            <button
              className="create-push-message-button"
              onClick={() => navigate("/push-message/create")}
            >
              {TextEnum.NEW_PUSH_MESSAGE}
            </button>
          </div>
          <DataTable
            dataSource={pushMessages}
            columns={Object.values(PushMessageColumns({ handleModalVisible }))}
            maxPage={pageCount}
            currentPage={page}
            setCurrentPage={useHandlePushMessagePageChange(
              dispatch,
              setSearchTerm,
            )}
            onRowClick={handleGetOne}
            functionDisabler={false}
            isAdminColumn={true}
          />
        </div>
        {errorMessage && (
          <MessageModal
            title=""
            message={errorMessage}
            onCloseClick={handleAlertMessage}
          />
        )}
      </main>
    </div>
  );
};
